import type { AuthenticationContextValue } from '@src/features/auth';
import settings from '@src/shared/config';
import type { AxiosInstance } from 'axios';
import axios from 'axios';
import { useCallback, useMemo } from 'react';
import { getTimezone } from '../lib/dateUtils';
import { is401 } from '../lib/httpUtils';

function createApiClient(): AxiosInstance {
  const instance = axios.create(settings.CLIENTS.origin);
  instance.defaults.withCredentials = true;
  instance.defaults.headers.common['Content-Type'] = 'application/json';
  instance.defaults.paramsSerializer = {
    serialize: (params) => {
      const usp = new URLSearchParams();
      Object.entries(params || {}).forEach(([key, value]) => {
        if (value === null || value === undefined) {
          return;
        }
        if (Array.isArray(value)) {
          value.forEach((v) => {
            usp.append(key, String(v));
          });
        } else if (typeof value === 'object') {
          usp.append(key, JSON.stringify(value));
        } else {
          usp.append(key, String(value));
        }
      });
      return usp.toString();
    },
  };

  return instance;
}

type RequestParams = {
  url?: string;
  method?: string;
  payload?: unknown;
  contentType?: string;
  useAuth?: boolean;
  headers?: Record<string, string>;
  options?: Record<string, unknown>;
  bodyOnDelete?: boolean;
};

export type APIClient = {
  request: (params: RequestParams) => Promise<unknown>;
};

type UseAPIClientBaseParams = {
  auth: AuthenticationContextValue;
  on401?: () => void;
};

export function useAPIClientBase(params: UseAPIClientBaseParams) {
  const { auth, on401 = () => {} } = params;

  const instances = useMemo(() => {
    const noauth = createApiClient();
    const authInstance = createApiClient();

    authInstance.interceptors.response.use(undefined, (error) => {
      if (is401(error.response)) {
        const { refresh = null } = auth.credentials || {};

        if (!refresh) {
          auth.unauthenticate();
          on401();
          return null;
        }

        return auth
          .refreshToken()
          .then((credentials) => {
            error.config.headers.Authorization = `Bearer ${credentials.access}`;
            return authInstance.request(error.config);
          })
          .catch(() => {
            auth.unauthenticate();
            on401();
            return null;
          });
      }

      return Promise.reject(error);
    });

    return { auth: authInstance, noauth };
  }, [auth, on401]);

  const request = useCallback(
    ({
      url = '',
      method = 'GET',
      payload = null,
      contentType = 'application/json',
      useAuth = true,
      headers = {},
      options = {},
      bodyOnDelete = false,
    }: RequestParams) => {
      const jsonOrFormData = payload;
      const instance = useAuth ? instances.auth : instances.noauth;
      const paramMethods = ['GET', ...(bodyOnDelete ? [] : ['DELETE'])];
      const dataOrParams = paramMethods.includes(method) ? 'params' : 'data';

      if (useAuth) {
        // Get access token from auth state
        const { access = null } = auth.credentials || {};
        // Access token not found
        if (!access) {
          auth.unauthenticate();
          on401();
          return Promise.reject(new Error('AccessToken not found.'));
        }

        // Access token found
        instance.defaults.headers.common.Authorization = `Bearer ${access}`;
      }

      instance.defaults.headers.common['Content-Type'] = contentType;
      instance.defaults.headers.common['User-Timezone'] = getTimezone();

      return new Promise((resolve, reject) => {
        instance
          .request({
            ...options,
            url,
            method,
            headers,
            [dataOrParams]: jsonOrFormData,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    [auth, on401, instances],
  );

  return { request };
}
